const DAY_MS = 86400000
const addCalendarDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
const addCalendarYears = (date, years) => new Date(date.getFullYear() + years, date.getMonth(), date.getDate())

export const parseLocalDate = (value) => {
  if (!value) return null
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  if (
    date.getFullYear() !== Number(match[1]) ||
    date.getMonth() !== Number(match[2]) - 1 ||
    date.getDate() !== Number(match[3])
  ) return null
  return date
}

export const formatDateYmd = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—'
  return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-')
}

export const formatMoney = (value) => Number(value || 0).toLocaleString('zh-CN', { maximumFractionDigits: 2 })

export const getTypeName = (type) => ({
  whole_life: '增额寿', annuity: '年金险', health: '健康险', cancer: '防癌险',
  life: '寿险', medical: '医疗险', accident: '意外险',
}[type] || '其他')

export const getPolicyTotalPremium = (policy) => (
  (Number(policy.annualPremium) || 0) +
  (policy.riders || []).reduce((sum, rider) => sum + (Number(rider.premium) || 0), 0)
)

export const getPolicyPaidYears = (policy) => {
  const start = parseLocalDate(policy.effectiveDate)
  if (!start || policy.lastPaidYear === null || policy.lastPaidYear === undefined || policy.lastPaidYear === '') return 0
  const term = Math.max(1, Number(policy.paymentTerm) || 1)
  return Math.max(0, Math.min(Number(policy.lastPaidYear) - start.getFullYear() + 1, term))
}

export const getPolicyCumulativePaid = (policy) => getPolicyTotalPremium(policy) * getPolicyPaidYears(policy)
export const getPolicyRemainingPayable = (policy) => getPolicyTotalPremium(policy) * Math.max(0, (Number(policy.paymentTerm) || 1) - getPolicyPaidYears(policy))

export const getNextConfirmedPaidYear = (policy) => {
  const start = parseLocalDate(policy.effectiveDate)
  if (!start) return null
  const termEnd = start.getFullYear() + Math.max(1, Number(policy.paymentTerm) || 1) - 1
  const current = policy.lastPaidYear === null || policy.lastPaidYear === undefined || policy.lastPaidYear === ''
    ? start.getFullYear() - 1
    : Number(policy.lastPaidYear)
  return Math.min(current + 1, termEnd)
}

export const getNextPaymentDate = (policy) => {
  const start = parseLocalDate(policy.effectiveDate)
  if (!start || policy.lastPaidYear === null || policy.lastPaidYear === undefined || policy.lastPaidYear === '') return null
  const term = Number(policy.paymentTerm) || 0
  if (term < 1 || getPolicyPaidYears(policy) >= term) return null
  return new Date(Number(policy.lastPaidYear) + 1, start.getMonth(), start.getDate())
}

export const getNextPaymentDateText = (policy, referenceDate = new Date()) => {
  if (policy.statusOverride === 'surrendered') return '已退保'
  if (policy.statusOverride === 'lapsed') return '已失效'
  if (!parseLocalDate(policy.effectiveDate)) return '日期待确认'
  if (policy.lastPaidYear === null || policy.lastPaidYear === undefined || policy.lastPaidYear === '') return '缴费记录待确认'
  if (getPolicyPaidYears(policy) >= (Number(policy.paymentTerm) || 0)) return '已交满'
  if (getPolicyStatusInfo(policy, referenceDate).code === 'lapsed') return '已失效'
  return formatDateYmd(getNextPaymentDate(policy))
}

const statusMap = {
  lapsed: { status: '失效（人工确认）', code: 'lapsed', tone: 'dark' },
  surrendered: { status: '退保', code: 'lapsed', tone: 'neutral' },
}

export const getPolicyStatusInfo = (policy, referenceDate = new Date()) => {
  if (statusMap[policy.statusOverride]) {
    return { ...statusMap[policy.statusOverride], diffDays: null }
  }
  const start = parseLocalDate(policy.effectiveDate)
  if (!start) return { status: '日期待确认', code: 'unknown', tone: 'warning', diffDays: null }
  const term = Number(policy.paymentTerm) || 0
  if (term < 1) return { status: '缴费期待确认', code: 'unknown', tone: 'warning', diffDays: null }
  if (policy.lastPaidYear === null || policy.lastPaidYear === undefined || policy.lastPaidYear === '') {
    return { status: '缴费记录待确认', code: 'unknown', tone: 'warning', diffDays: null }
  }
  if (getPolicyPaidYears(policy) >= term) return { status: '有效（已交满）', code: 'valid_full', tone: 'success', diffDays: null }

  const nextDate = getNextPaymentDate(policy)
  const today = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate())
  const diffDays = Math.round((nextDate - today) / DAY_MS)
  if (diffDays > 30) return { status: '有效', code: 'valid', tone: 'success', diffDays }
  if (diffDays >= 0) return { status: '待缴费', code: 'pending', tone: 'warning', diffDays }
  const graceEnd = addCalendarDays(nextDate, 60)
  if (today <= graceEnd) return { status: '宽限期内', code: 'grace', tone: 'danger', diffDays }
  const suspensionEnd = addCalendarYears(graceEnd, 2)
  if (today <= suspensionEnd) return { status: '中止期', code: 'suspended', tone: 'neutral', diffDays }
  return { status: '失效', code: 'lapsed', tone: 'dark', diffDays }
}

export const enrichPolicy = (policy, referenceDate) => ({
  ...policy,
  statusInfo: getPolicyStatusInfo(policy, referenceDate),
  totalPremium: getPolicyTotalPremium(policy),
})

export const getMainCoverage = (policy) => {
  if (policy.type === 'health' || policy.type === 'cancer') return Number(policy.severeCoverage) || 0
  if (policy.type === 'annuity') return Number(policy.estimatedTotal) || 0
  return Number(policy.coverage) || 0
}

const itemId = (policyId, kind, index = null) => kind === 'main' ? `${policyId}_main` : `${policyId}_rider_${index}`
const normalizedText = (value) => String(value || '').replace(/\s+/g, '')
const manuallyShared = (item) => Boolean(item?.shareCoverage && item?.shareWithId)

export const isSharedCoverageRider = (policy, rider) => {
  if (manuallyShared(rider)) return true
  if (rider.type !== '重疾险') return false
  const riderAmount = Number(rider.coverage) || 0
  const mainAmount = Number(policy.coverage) || 0
  const isLifeCarrier = ['life', 'whole_life'].includes(policy.type) || /终身寿|寿险|两全/.test(normalizedText(policy.productName))
  if (!isLifeCarrier || riderAmount <= 0 || mainAmount <= 0) return false
  const text = normalizedText(`${policy.productName || ''}${policy.note || ''}${rider.name || ''}`)
  return /提前给付|共享保额|共用保额|占用主险|主合同.*减少|主险.*减少|给付后.*减少|保额.*降为?0/.test(text) ||
    (rider.guaranteePeriod === '终身' && Math.abs(mainAmount - riderAmount) < 1 && /重疾|重大疾病/.test(text))
}

const hasIncomingShare = (policies, id) => policies.some((policy) => (
  policy.shareWithId === id || (policy.riders || []).some((rider) => rider.shareWithId === id)
))

const shareGroup = (policies, policy, kind, index, item) => {
  const id = itemId(policy.id, kind, index)
  if (manuallyShared(item)) return `share_${item.shareWithId}`
  if (kind === 'main' && hasIncomingShare(policies, id)) return `share_${id}`
  if (kind === 'rider' && isSharedCoverageRider(policy, item)) return `share_${itemId(policy.id, 'main')}`
  return ''
}

const shouldCountLifeMain = (policies, policy) => {
  if (manuallyShared(policy)) return false
  const mainId = itemId(policy.id, 'main')
  if (hasIncomingShare(policies, mainId)) return false
  return !(policy.riders || []).some((rider) => isSharedCoverageRider(policy, rider))
}

const sumWithShareGroups = (details, excludedIds) => {
  let total = 0
  const groups = new Map()
  details.forEach((detail) => {
    if (excludedIds.includes(detail.id)) return
    if (!detail.shareGroupId) total += detail.amount
    else groups.set(detail.shareGroupId, Math.max(groups.get(detail.shareGroupId) || 0, detail.amount))
  })
  return total + [...groups.values()].reduce((sum, value) => sum + value, 0)
}

export const calculateGaps = (sourcePolicies, goals, adjustments) => {
  const policies = sourcePolicies.map((policy) => enrichPolicy(policy))
  const data = {
    health: { key: 'health', name: '健康保障', details: [], target: Number(goals.health) || 0, tone: 'danger' },
    life: { key: 'life', name: '身价传承', details: [], target: Number(goals.life) || 0, tone: 'primary' },
    retirement: { key: 'retirement', name: '养老储备', details: [], target: Number(goals.retirement) || 0, tone: 'ai' },
    education: { key: 'education', name: '教育储备', details: [], target: Number(goals.education) || 0, tone: 'success' },
  }
  policies.forEach((policy) => {
    if (policy.statusInfo.code === 'lapsed') return
    const name = policy.productName || '未命名保单'
    if (policy.type === 'health' && policy.guaranteePeriod === '终身' && Number(policy.severeCoverage) > 0) {
      data.health.details.push({ id: itemId(policy.id, 'main'), name: `${name}（主险）`, amount: Number(policy.severeCoverage), shareGroupId: shareGroup(policies, policy, 'main', null, policy) })
    }
    if (policy.type === 'life' && Number(policy.coverage) > 0 && shouldCountLifeMain(policies, policy)) {
      data.life.details.push({ id: itemId(policy.id, 'main'), name: `${name}（主险）`, amount: Number(policy.coverage), shareGroupId: '' })
    }
    if (policy.type === 'whole_life') {
      if (Number(policy.cv18) > 0) data.education.details.push({ id: `${policy.id}_cv18`, name: `${name}（18岁现价）`, amount: Number(policy.cv18), shareGroupId: '' })
      if (Number(policy.cv60) > 0) data.retirement.details.push({ id: `${policy.id}_cv60`, name: `${name}（60岁现价）`, amount: Number(policy.cv60), shareGroupId: '' })
    }
    if (policy.type === 'annuity' && Number(policy.estimatedTotal) > 0) {
      const key = policy.annuityFunc === 'retirement' ? 'retirement' : 'education'
      data[key].details.push({ id: `${policy.id}_annuity`, name: `${name}（预计领取）`, amount: Number(policy.estimatedTotal), shareGroupId: '' })
    }
    ;(policy.riders || []).forEach((rider, index) => {
      const amount = Number(rider.coverage) || 0
      if (amount <= 0) return
      if (rider.type === '寿险') data.life.details.push({ id: itemId(policy.id, 'rider', index), name: `${name} · ${rider.name || '附加寿险'}`, amount, shareGroupId: shareGroup(policies, policy, 'rider', index, rider) })
      if (rider.type === '重疾险' && rider.guaranteePeriod === '终身') data.health.details.push({ id: itemId(policy.id, 'rider', index), name: `${name} · ${rider.name || '附加重疾'}`, amount, shareGroupId: shareGroup(policies, policy, 'rider', index, rider) })
    })
  })
  return Object.values(data).map((gap) => {
    const adjustment = adjustments[gap.key] || { excludedIds: [], customItems: [] }
    const excludedIds = Array.isArray(adjustment.excludedIds) ? adjustment.excludedIds : []
    const details = gap.details.map((detail) => ({ ...detail, included: !excludedIds.includes(detail.id), isAuto: true }))
    const customItems = (adjustment.customItems || []).map((item) => ({ ...item, amount: Number(item.amount) || 0, included: true, isAuto: false }))
    const current = sumWithShareGroups(gap.details, excludedIds) + customItems.reduce((sum, item) => sum + item.amount, 0)
    const ratio = gap.target > 0 ? Math.min(100, Math.round(current / gap.target * 100)) : (current > 0 ? 100 : 0)
    return { ...gap, details: [...details, ...customItems], current, shortage: Math.max(0, gap.target - current), ratio }
  })
}

export const paymentTimeline = (policies, months = 12, referenceDate = new Date()) => {
  const end = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + months, referenceDate.getDate())
  return policies.map((policy) => ({ ...enrichPolicy(policy, referenceDate), nextDate: getNextPaymentDate(policy) }))
    .filter((policy) => policy.nextDate && policy.nextDate <= end)
    .sort((a, b) => a.nextDate - b.nextDate)
}

export const policyTypeDistribution = (policies) => {
  const counts = new Map()
  policies.map((policy) => enrichPolicy(policy)).filter((policy) => policy.statusInfo.code !== 'lapsed').forEach((policy) => {
    const name = getTypeName(policy.type)
    counts.set(name, (counts.get(name) || 0) + 1)
  })
  const total = [...counts.values()].reduce((sum, value) => sum + value, 0)
  return [...counts.entries()].map(([name, count]) => ({ name, count, ratio: total ? Math.round(count / total * 100) : 0 }))
}
